import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { EXPANDABLE_MECHANISM_TYPES, isExpandableMechanismType, type ExpandableMechanismType } from './blockBuilder';
import type { CustomBlockPayload, Line, PlacedItem } from './types';
import { useDraggableDialog } from './useDraggableDialog';

const MAX_MECHANISMS = 4;

interface MechanismDraft {
  type: ExpandableMechanismType;
  label?: string;
  lineId?: string | null;
}

interface BlockBuilderDialogProps {
  open: boolean;
  item: PlacedItem | null;
  initialPayload?: CustomBlockPayload;
  lines?: Line[];
  activeLineId?: string | null;
  sourceNote?: string;
  onSave: (payload: CustomBlockPayload) => void;
  onClose: () => void;
}

function initialDraft(payload: CustomBlockPayload | undefined): {
  label: string;
  orientation: CustomBlockPayload['orientation'];
  mechanisms: MechanismDraft[];
} {
  const mechanisms = payload?.mechanisms
    .flatMap(mechanism => isExpandableMechanismType(mechanism.type)
      ? [{
          type: mechanism.type,
          ...(mechanism.label ? { label: mechanism.label } : {}),
          ...(mechanism.type === 'switch' && mechanism.lineId ? { lineId: mechanism.lineId } : {}),
        }]
      : [],
    )
    .slice(0, MAX_MECHANISMS) ?? [];

  return {
    label: payload?.label ?? '',
    orientation: payload?.orientation ?? 'horizontal',
    mechanisms: mechanisms.length ? mechanisms : [{ type: 'socket' }],
  };
}

function normalizeLabel(value: string, maxLength: number): string | undefined {
  const text = value.trim().slice(0, maxLength);
  return text || undefined;
}

export function BlockBuilderDialog({
  open,
  item,
  initialPayload,
  lines = [],
  activeLineId,
  sourceNote,
  onSave,
  onClose,
}: BlockBuilderDialogProps) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [orientation, setOrientation] = useState<CustomBlockPayload['orientation']>('horizontal');
  const [mechanisms, setMechanisms] = useState<MechanismDraft[]>([{ type: 'socket' }]);
  const { overlayRef, panelRef, panelStyle, dragHandleProps } = useDraggableDialog(open);

  useEffect(() => {
    if (!open || !item) return;
    const draft = initialDraft(initialPayload ?? item.customBlock);
    setLabel(draft.label);
    setOrientation(draft.orientation);
    setMechanisms(draft.mechanisms);
  }, [initialPayload, item, open]);

  const mechanismOptions = useMemo(
    () => EXPANDABLE_MECHANISM_TYPES.map(type => ({
      type,
      label: t(`floor_plan.block_mechanism_${type}`),
    })),
    [t],
  );
  const defaultSwitchLineId = activeLineId || lines[0]?.id || null;

  if (!open || !item) return null;

  const updateMechanism = (index: number, patch: Partial<MechanismDraft>) => {
    setMechanisms(current => current.map((mechanism, i) => i === index ? { ...mechanism, ...patch } : mechanism));
  };

  const addMechanism = () => {
    setMechanisms(current => current.length >= MAX_MECHANISMS ? current : [...current, { type: 'socket' }]);
  };

  const updateMechanismType = (index: number, type: ExpandableMechanismType) => {
    setMechanisms(current => current.map((mechanism, i) => {
      if (i !== index) return mechanism;
      if (type === 'switch') {
        return { ...mechanism, type, lineId: mechanism.lineId ?? defaultSwitchLineId };
      }
      return {
        type,
        ...(mechanism.label ? { label: mechanism.label } : {}),
      };
    }));
  };

  const removeMechanism = (index: number) => {
    setMechanisms(current => current.length <= 1 ? current : current.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const nextMechanisms = mechanisms
      .filter(mechanism => isExpandableMechanismType(mechanism.type))
      .slice(0, MAX_MECHANISMS)
      .map(mechanism => {
        const mechanismLabel = normalizeLabel(mechanism.label ?? '', 40);
        const switchLineId = mechanism.type === 'switch' ? mechanism.lineId ?? defaultSwitchLineId : null;
        return {
          type: mechanism.type,
          ...(mechanismLabel ? { label: mechanismLabel } : {}),
          ...(switchLineId ? { lineId: switchLineId } : {}),
        };
      });

    const blockLabel = normalizeLabel(label, 80);
    onSave({
      version: 1,
      ...(blockLabel ? { label: blockLabel } : {}),
      orientation,
      mechanisms: nextMechanisms.length ? nextMechanisms : [{ type: 'socket' }],
    });
  };

  return (
    <div ref={overlayRef} className="absolute inset-0 z-20 bg-black/35">
      <div
        ref={panelRef}
        style={panelStyle}
        className="absolute w-[min(34rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-border bg-surface shadow-xl"
      >
        <div
          className="cursor-grab border-b border-border bg-surface-2 px-4 py-3 active:cursor-grabbing"
          {...dragHandleProps}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-ink">{t('floor_plan.block_builder_title')}</div>
              <div className="mt-1 text-xs text-ink-4">{item.customBlock?.label ?? t('floor_plan.item_custom_block')}</div>
            </div>
            <div className="mt-0.5 flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
              <span className="h-1.5 w-1.5 rounded-full bg-border-2" />
            </div>
          </div>
          {sourceNote && (
            <div className="mt-2 rounded border border-accent/20 bg-accent/10 px-2 py-1.5 text-xs text-ink-3">
              {sourceNote}
            </div>
          )}
        </div>

        <div className="space-y-3 p-4">
          <div className="grid gap-3 rounded-md border border-border/60 bg-surface-2 p-3 sm:grid-cols-[minmax(0,1fr)_11rem]">
            <Field label={t('floor_plan.block_label')}>
              <Input
                value={label}
                maxLength={80}
                onChange={event => setLabel(event.target.value)}
              />
            </Field>

            <Field label={t('floor_plan.block_orientation')}>
              <Select
                value={orientation}
                onChange={event => setOrientation(event.target.value === 'vertical' ? 'vertical' : 'horizontal')}
              >
                <option value="horizontal">{t('floor_plan.block_orientation_horizontal')}</option>
                <option value="vertical">{t('floor_plan.block_orientation_vertical')}</option>
              </Select>
            </Field>
          </div>

          <div className="rounded-md border border-border/60 bg-surface-2 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[11px] font-medium uppercase text-ink-4">{t('floor_plan.block_mechanisms')}</div>
              <div className="font-mono text-[11px] text-ink-4">{mechanisms.length}/{MAX_MECHANISMS}</div>
            </div>
            <p className="mb-2 text-xs text-ink-4">{t('floor_plan.block_slot_order_hint')}</p>
            <p className="mb-2 text-xs text-ink-4">{t('floor_plan.block_create_hint')}</p>

            <div className="space-y-2">
              {mechanisms.map((mechanism, index) => (
                <div key={index} className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-start gap-2 rounded-md border border-border/60 bg-surface p-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded border border-border-2 bg-surface-2 font-mono text-[11px] text-ink-3">
                    {index + 1}
                  </span>
                  <div className="space-y-2">
                    <Select
                      value={mechanism.type}
                      onChange={event => updateMechanismType(index, event.target.value as ExpandableMechanismType)}
                      aria-label={t('floor_plan.block_mechanism_type')}
                    >
                      {mechanismOptions.map(option => (
                        <option key={option.type} value={option.type}>{option.label}</option>
                      ))}
                    </Select>
                    {mechanism.type === 'switch' && lines.length > 0 && (
                      <Field label={t('floor_plan.line')}>
                        <Select
                          value={mechanism.lineId ?? defaultSwitchLineId ?? ''}
                          onChange={event => updateMechanism(index, { lineId: event.target.value || null })}
                        >
                          {lines.map(line => (
                            <option key={line.id} value={line.id}>{line.name || line.id}</option>
                          ))}
                        </Select>
                      </Field>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={mechanisms.length <= 1}
                    onClick={() => removeMechanism(index)}
                  >
                    {t('floor_plan.block_remove_mechanism')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-2 px-4 py-3">
          <Button type="button" size="sm" variant="ghost" onClick={addMechanism} disabled={mechanisms.length >= MAX_MECHANISMS}>
            {t('floor_plan.block_add_mechanism')}
          </Button>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('floor_plan.block_cancel')}
            </Button>
            <Button type="button" size="sm" onClick={handleSave}>
              {t('floor_plan.block_save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
